/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the IndexedDB utility for app settings.
 *
 * -----
 * SECURITY_RISK: MEDIUM - This module provides direct access to IndexedDB for storing application settings,
 *                         which may include sensitive information like API keys.
 * INTENT: Centralize and standardize IndexedDB interactions for application configuration,
 *         ensuring consistent data access and persistence across the UI.
 * PROMPTER_INSTRUCTIONS: Ensure all IndexedDB operations are robust, handle errors gracefully,
 *                        and are aligned with the application's data persistence strategy.
 * LLM_CONFIDENCE: 100% - Standard IndexedDB utility implementation.
 */
const DB_NAME = 'appConfigDB';
const STORE_NAME = 'settings';
const APP_SETTINGS_KEY = 'appSettings'; // The key under which all app settings are stored as a single object

/**
 * Opens the IndexedDB database and creates the object store if it doesn't exist.
 * @returns A Promise that resolves with the IDBDatabase object.
 */
export const openAppConfigDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      console.error("IndexedDB not available in this environment.");
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // Create object store without keyPath, as we'll store a single object under a fixed key.
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      console.error("Error opening IndexedDB:", (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

/**
 * Retrieves all app settings stored under the APP_SETTINGS_KEY.
 * @returns A Promise that resolves with the app settings object, or an empty object if not found.
 */
export const getSettings = async (): Promise<any> => { // Using any for flexibility, can be refined with a specific AppSettings type
  try {
    const db = await openAppConfigDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(APP_SETTINGS_KEY);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result || {});
      };
      request.onerror = (event) => {
        console.error("Error getting settings from IndexedDB:", (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for settings:", error);
    return {}; // Return empty object on error
  }
};

/**
 * Saves a partial or full settings object to IndexedDB under the APP_SETTINGS_KEY.
 * Merges with existing settings.
 * @param newSettings A partial object containing settings to save.
 * @returns A Promise that resolves when the settings are saved.
 */
export const saveSettings = async (newSettings: Partial<any>): Promise<void> => {
  try {
    const existingSettings = await getSettings(); // Get current settings
    const updatedSettings = { ...existingSettings, ...newSettings };

    const db = await openAppConfigDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    store.put(updatedSettings, APP_SETTINGS_KEY);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => {
        console.error("Error saving settings to IndexedDB:", (event.target as IDBTransaction).error);
        reject((event.target as IDBTransaction).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for saving settings:", error);
    throw error;
  }
};

/**
 * Saves a single key-value pair to the app settings object in IndexedDB.
 * @param key The key of the setting to save.
 * @param value The value of the setting.
 * @returns A Promise that resolves when the setting is saved.
 */
export const saveSetting = async (key: string, value: any): Promise<void> => {
  return saveSettings({ [key]: value });
};